import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, Polyline, ZoomControl, useMap } from 'react-leaflet'
import { basemapUrl, BASEMAP_ATTRIB } from '../lib/basemap'
import { DivIcon, type Layer } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { FiLoader, FiAlertCircle, FiShare2, FiX, FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi'

// Painel "Rede" (ex-"Cobertura", renomeado 2026-07-06): mapa único, sem header
// nem tabela — só o mapa, ocupando o espaço todo. Contorno do MUNICÍPIO
// preenchido (não pin) — cinza (área a desbloquear) pras cidades sem líder,
// verde pras cobertas (consultoras tipo 3 × líderes tipo 2 no cadastro).
// Cinza em vez de vermelho: vermelho comunica "perdido/cancelado", errado pro
// caso — é território ainda não atingido. Contorno vem de
// public/geo/ms-municipios.geojson
// (extensão .geojson de propósito — o nginx bloqueia qualquer *.json estático)
// (malha IBGE-MS, github.com/tbrugz/geodata-br). Casamento por nome
// normalizado (maiúsculo sem acento) com `cadastro_clientes.endereco_cidade`.
// Cidade sem contorno encontrado (nome não bate, ou o dataset é de antes da
// criação do município — falta Paraíso das Águas, por ex.) cai num marcador
// de ponto, pra não desaparecer da tela.
//
// Camada "Rede líder↔lead": sobreposta ao MESMO mapa, sempre ligada (era um
// toggle, virou permanente — é o motivo do painel se chamar "Rede" agora) —
// pin de líder e pin de lead, ambos o MESMO círculo (pontos homogêneos, só a
// cor muda) com um laço animado entre os dois, montando a "teia" de
// encaminhamentos por cima do contorno de cobertura. KPIs, legenda, filtros
// (líder/distrito/período) e detalhes (clique num pin) vivem todos em painéis
// flutuantes SOBRE o mapa — não tem mais header/toolbar acima dele.

interface CoverageCity {
  cidade: string
  consultoras: number
  lideres: number
  consultorasPorLider: number | null
  gap: boolean
  // Frente 4 (roadmap Pareto): receita Oracle YTD × leads YTD do município.
  // oportunidade = município no corte de Pareto da receita com ≤1 lead no ano.
  receita: number | null
  receitaShare: number | null
  leadsYtd: number
  oportunidade: boolean
  lat: number | null
  lng: number | null
}
interface CoverageData {
  success: boolean
  summary: {
    cidades: number
    cidadesSemLider: number
    consultorasSemLider: number
    totalConsultoras: number
    totalLideres: number
  }
  cidades: CoverageCity[]
}
interface MunicipioFeature {
  type: 'Feature'
  properties: { id: string; name: string; description?: string; coverage?: CoverageCity }
  geometry: any
}

const CAMPO_GRANDE: [number, number] = [-20.4697, -54.6201]

const normName = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim()

// Tema (claro/escuro) — mesmo padrão do Mapa de Ativas (classe .dark no <html>).
const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  )
  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setIsDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return isDark
}

// Marcador de fallback (cidade sem contorno no dataset IBGE) — mesmo estilo
// visual do polígono (cinza=área a desbloquear / verde=coberta), só que em
// ponto. Um par de círculos simples (não pin/teardrop) — mesmo desenho,
// só a cor muda, pra não destoar visualmente um do outro. Fábrica (não
// constante) porque agora também esmaece quando o isolamento de líder/lead
// está ativo — precisa recriar o ícone a cada render pro `dim` valer. Só
// opacidade (SEM blur): `filter:blur()` em ~500 ícones simultâneos deixava o
// zoom/pan travado (CPU disparava) — opacidade sozinha já esmaece bem e é
// praticamente grátis pro navegador renderizar.
const cityDotIcon = (color: string, dim: boolean) => new DivIcon({
  html: `<div style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.35));opacity:${dim ? 0.3 : 1}">
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" fill="${color}" stroke="#fff" stroke-width="1.5"/>
    </svg>
  </div>`,
  iconSize: [18, 18], iconAnchor: [9, 9], popupAnchor: [0, -9], className: 'leaflet-coverage-dot',
})

interface NetworkPair {
  assignmentId: string
  leaderCode: string
  leaderName: string
  leaderPhone: string | null
  leaderLat: number
  leaderLng: number
  distrito: string
  grupo: string
  leadName: string
  leadPhone: string | null
  leadCpf: string | null
  leadAddress: string | null
  leadCity: string | null
  leadMotivation: string | null
  leadInterests: string[] | null
  leadCep: string | null
  leadLat: number
  leadLng: number
  status: string
  leaderOutcome: 'success' | 'trying' | 'failure' | null
  convertedOracle: boolean
  convertedAt: string | null
  assignedAt: string | null
  firstMessageAt: string | null
  cadastroCompletoAt: string | null
}
interface NetworkData {
  success: boolean
  range: { from: string; to: string }
  total: number
  resolved: number
  truncated?: boolean
  limit?: number
  pairs: NetworkPair[]
}

// Pontos homogêneos — mesmo círculo, mesmo tamanho, pra líder e lead; só a
// cor muda. Roxo = líder (identidade fixa, não tem desfecho). Azul = virou
// consultora (confirmado no Oracle, o resultado final — prioridade sobre
// tudo). Rosa = lead novo/sem resposta da líder ainda (neutro — nada
// aconteceu, não é nem bom nem ruim; era cinza, ficou ruim visualmente).
// Verde = deu certo (desfecho positivo, cor própria — não pode dividir com
// "sem resposta", senão tudo vira verde e perde o sinal). Amarelo = ainda
// tentando. Vermelho = não deu certo.
const LEADER_COLOR = '#8b5cf6'
const NEW_COLOR = '#ec4899'
const SUCCESS_COLOR = '#22c55e'
const TRYING_COLOR = '#facc15'
const FAILURE_COLOR = '#ef4444'
const CONVERTED_COLOR = '#3b82f6'

const leadColor = (p: Pick<NetworkPair, 'leaderOutcome' | 'convertedOracle'>) => {
  if (p.convertedOracle) return CONVERTED_COLOR
  if (p.leaderOutcome === 'trying') return TRYING_COLOR
  if (p.leaderOutcome === 'failure') return FAILURE_COLOR
  if (p.leaderOutcome === 'success') return SUCCESS_COLOR
  return NEW_COLOR // sem interação ainda (null)
}

// Mesmo bucket que decide a cor, só que como chave — usada pelos toggles da
// legenda (mostrar/esconder categoria no mapa).
type NetCategory = 'new' | 'success' | 'trying' | 'failure' | 'converted'
const pairCategory = (p: Pick<NetworkPair, 'leaderOutcome' | 'convertedOracle'>): NetCategory => {
  if (p.convertedOracle) return 'converted'
  if (p.leaderOutcome === 'trying') return 'trying'
  if (p.leaderOutcome === 'failure') return 'failure'
  if (p.leaderOutcome === 'success') return 'success'
  return 'new'
}

const outcomeLabel = (p: Pick<NetworkPair, 'leaderOutcome' | 'convertedOracle'>) =>
  p.convertedOracle ? 'Virou consultora'
    : p.leaderOutcome === 'success' ? 'Deu certo'
    : p.leaderOutcome === 'failure' ? 'Não deu certo'
    : p.leaderOutcome === 'trying' ? 'Ainda tentando'
    : 'Sem resposta ainda'

// Mesmo padrão usado em ForwardModal.tsx/IncentivePanel.tsx — sem util
// compartilhado no projeto, cada componente formata telefone localmente.
const formatPhone = (p: string): string => {
  const d = p.replace(/\D/g, '')
  if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
  if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return p
}
const fmtDateTime = (s: string | null) => s ? new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', dateStyle: 'short', timeStyle: 'short' }) : '—'
const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('pt-BR', { timeZone: 'America/Campo_Grande' }) : '—'

// Timeline de movimentações de um lead — usada na lista expansível da líder.
const leadTimeline = (p: NetworkPair) => {
  const items: { label: string; date: string }[] = []
  if (p.firstMessageAt) items.push({ label: 'Primeira mensagem', date: p.firstMessageAt })
  if (p.assignedAt) items.push({ label: 'Encaminhado à líder', date: p.assignedAt })
  if (p.cadastroCompletoAt) items.push({ label: 'Cadastro completo', date: p.cadastroCompletoAt })
  if (p.convertedOracle && p.convertedAt) items.push({ label: 'Virou consultora', date: p.convertedAt })
  return items.sort((a, b) => a.date.localeCompare(b.date))
}

// Fábrica única de ícone (círculo) — usada tanto pra líder quanto pra lead,
// só muda a cor. "Pontos homogêneos" (pedido do usuário): mesmo tamanho,
// mesma forma, sem diferenciar por estrela/pin. `dim` esmaece o pin quando o
// isolamento líder+lead está ativo e este pin NÃO é o foco — só opacidade
// (sem blur: em ~500 ícones simultâneos, blur disparava a CPU e travava o
// zoom/pan; opacidade sozinha já esmaece bem e é praticamente grátis).
const dotIcon = (color: string, dim = false) => new DivIcon({
  html: `<div style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));opacity:${dim ? 0.28 : 1}">
    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" fill="${color}" stroke="#fff" stroke-width="2"/>
    </svg>
  </div>`,
  iconSize: [16, 16], iconAnchor: [8, 8], popupAnchor: [0, -8], className: 'leaflet-network-dot',
})
const leadIcon = dotIcon

// Escapa texto que vai para HTML montado à mão. `cidade` vem do banco (do
// cadastro sincronizado do Oracle), não é constante do código — e é
// interpolada em template string que depois entra no DOM via
// dangerouslySetInnerHTML / L.popup().setContent(). Sem escape, um nome de
// cidade com `<` vira marcação.
const escHtml = (v: unknown) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

function CoveragePopupHtml(c: CoverageCity) {
  const fmtR$ = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
  return `<div style="font-size:12px;line-height:1.5">
    <div style="font-weight:600">${escHtml(c.cidade)}</div>
    <div>${c.consultoras} consultora${c.consultoras === 1 ? '' : 's'}</div>
    <div>${c.lideres} líder${c.lideres === 1 ? '' : 'es'}</div>
    ${c.gap
      ? '<div style="color:#6b7280;font-weight:600;margin-top:4px">Sem líder local — área a desbloquear</div>'
      : `<div style="color:#6b7280">${c.consultorasPorLider} consultoras/líder</div>`}
    ${c.receita != null ? `<div style="margin-top:4px;color:#059669">Receita ${new Date().getFullYear()}: <b>${fmtR$(c.receita)}</b> (${c.receitaShare}%)</div>
    <div style="color:#6b7280">${c.leadsYtd} lead${c.leadsYtd === 1 ? '' : 's'} no ano</div>` : ''}
    ${c.oportunidade ? '<div style="color:#d97706;font-weight:600;margin-top:2px">Oportunidade: receita alta, quase sem leads</div>' : ''}
  </div>`
}

// Zoom/isolamento — voa até enquadrar líder+lead(s) em foco; ao fechar,
// volta a enquadrar tudo que está plotado. wasFocusedRef evita disparar o
// "volta a enquadrar tudo" no primeiro carregamento (só reage à transição
// foco→sem foco, não ao estado inicial).
// Aplica o centro/zoom da praça UMA vez, quando /api/mapa/config chega.
// Precisa ser efeito, e não prop `center` do MapContainer: o Leaflet só lê
// center/zoom na criação, e desde a correção do remonta-mapa o container
// nasce antes de a config existir. O ref garante que só acontece uma vez —
// depois disso o mapa é do usuário.
function PracaViewEffect({ center }: { center: { lat: number; lng: number; zoom?: number } | null }) {
  const map = useMap()
  const aplicado = useRef(false)
  useEffect(() => {
    if (!center || aplicado.current) return
    aplicado.current = true
    map.setView([center.lat, center.lng], center.zoom ?? 7)
  }, [center, map])
  return null
}

function NetworkFocusEffect({ focusPositions, allPositions, onBackgroundClick }: { focusPositions: [number, number][] | null; allPositions: [number, number][]; onBackgroundClick: () => void }) {
  const map = useMap()
  const wasFocusedRef = useRef(false)
  // Enquanto o flyToBounds está animando, os pins ficam se movendo na tela —
  // um clique num pin DIFERENTE nesse meio tempo erra o alvo (ele já saiu do
  // pixel) e acaba caindo no mapa vazio por baixo, o que disparava
  // onBackgroundClick e fechava tudo de novo (bug real: "precisa clicar
  // várias vezes"). Ignorar cliques de fundo enquanto anima resolve.
  const flyingRef = useRef(false)
  useEffect(() => {
    if (focusPositions && focusPositions.length > 0) {
      wasFocusedRef.current = true
      map.flyToBounds(focusPositions, { padding: [70, 70], maxZoom: 14, duration: 0.4 })
    } else if (wasFocusedRef.current) {
      wasFocusedRef.current = false
      if (allPositions.length > 0) map.flyToBounds(allPositions, { padding: [40, 40], duration: 0.4 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusPositions])
  useEffect(() => {
    const onStart = () => { flyingRef.current = true }
    const onEnd = () => { flyingRef.current = false }
    map.on('movestart', onStart)
    map.on('moveend', onEnd)
    return () => { map.off('movestart', onStart); map.off('moveend', onEnd) }
  }, [map])
  // Clique em área vazia do mapa (fora de qualquer pin/polígono — marcadores e
  // paths do Leaflet não propagam clique pro mapa por padrão) fecha o
  // isolamento, igual ao botão "X" do painel.
  useEffect(() => {
    const handler = () => { if (!flyingRef.current) onBackgroundClick() }
    map.on('click', handler)
    return () => { map.off('click', handler) }
  }, [map, onBackgroundClick])
  return null
}

export function LeaderCoverageView() {
  const { authenticatedFetch } = useAuth()
  const isDark = useIsDark()
  const [data, setData] = useState<CoverageData | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [municipios, setMunicipios] = useState<MunicipioFeature[] | null>(null)
  // Configuração da praça (UFs, centro, malha de municípios). O Mapa de Ativas
  // já era geografia-agnóstico desde o commit 20e86d3; este painel tinha ficado
  // para trás com Campo Grande e a malha de MS cravadas no código — e por isso
  // não funcionava na praça Planalto (Tocantins).
  const [mapCfg, setMapCfg] = useState<{ center?: { lat: number; lng: number; zoom: number }; geojsonUrl?: string } | null>(null)
  const [network, setNetwork] = useState<NetworkData | null>(null)
  const [networkLoading, setNetworkLoading] = useState(false)
  const [networkErr, setNetworkErr] = useState<string | null>(null)
  const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const [netFrom, setNetFrom] = useState(() => isoDate(new Date(Date.now() - 30 * 86400000)))
  const [netTo, setNetTo] = useState(() => isoDate(new Date()))
  const [netLeaderFilter, setNetLeaderFilter] = useState('')
  const [netDistritoFilter, setNetDistritoFilter] = useState('')
  // Busca livre — nome do lead/líder, grupo, distrito, telefone. Achou = isola
  // no mapa igual clicar num pin (reusa focusPositions/isFocused* abaixo).
  const [searchTerm, setSearchTerm] = useState('')
  // Toggles da legenda — cada categoria some/aparece no mapa ao clicar nela.
  const [catVisible, setCatVisible] = useState<Record<'leader' | NetCategory, boolean>>({
    leader: true, new: true, success: true, trying: true, failure: true, converted: true,
  })
  const toggleCat = (k: 'leader' | NetCategory) => setCatVisible(v => ({ ...v, [k]: !v[k] }))
  // Camada "Receita × Leads" (frente 4 do roadmap Pareto): recolore os polígonos
  // dos municípios pela receita Oracle YTD e destaca oportunidades (receita alta,
  // quase sem leads). Desligada por padrão — a visão default segue sendo cobertura.
  const [receitaLayer, setReceitaLayer] = useState(false)
  // Painel flutuante de detalhes — aberto ao clicar num pin (líder ou lead).
  const [detail, setDetail] = useState<{ type: 'lead'; pair: NetworkPair } | { type: 'leader'; leaderCode: string } | null>(null)
  // Linha expandida na lista "leads recebidos" do painel da líder (assignmentId).
  const [expandedLead, setExpandedLead] = useState<string | null>(null)
  useEffect(() => { setDetail(null); setExpandedLead(null); setSearchTerm('') }, [netFrom, netTo])

  const load = useCallback(async () => {
    setLoading(true); setErr(null)
    try {
      const r = await authenticatedFetch('/api/leader-coverage')
      const d = await r.json()
      if (!d || !d.success) { setErr(d?.error || 'Erro ao carregar a cobertura'); setData(null) }
      else setData(d)
    } catch (e: any) {
      setErr(e?.message || 'Erro de rede'); setData(null)
    } finally { setLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => { load() }, [load])

  // Configuração da praça, antes de qualquer coisa geográfica.
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      try {
        const r = await authenticatedFetch('/api/mapa/config')
        const j = await r.json()
        if (!cancelado && j && !j.error) setMapCfg(j)
      } catch { /* fallback: constantes abaixo */ }
    })()
    return () => { cancelado = true }
  }, [authenticatedFetch])

  // Contorno dos municípios — vem da API da praça (que fatia a malha nacional
  // do IBGE pelas UFs configuradas). Era um asset estático de MS servido sem
  // auth (`/geo/ms-municipios.geojson`), o que quebrava em qualquer outra praça.
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      try {
        const r = await authenticatedFetch(mapCfg?.geojsonUrl || '/api/mapa/municipios-geojson')
        const d = await r.json()
        if (!cancelado) setMunicipios(d.features || [])
      } catch { if (!cancelado) setMunicipios([]) }
    })()
    return () => { cancelado = true }
  }, [authenticatedFetch, mapCfg?.geojsonUrl])

  // Rede líder↔lead: carregada só quando o mapa é aberto (evita geocodificar
  // CEP à toa se o usuário nunca sai da Tabela). Período controla o que é
  // BUSCADO (recarrega o backend); líder/distrito filtram só no cliente —
  // já temos os pares em mão, filtrar local é instantâneo ("é pra ser
  // visual"), sem esperar rede a cada troca de líder/distrito. Mapa e Rede
  // são a mesma visualização — a rede é uma camada a mais sobre o mesmo mapa
  // de cobertura, não uma aba separada (toggle "Mostrar rede" liga/desliga).
  const loadNetwork = useCallback(async () => {
    setNetworkLoading(true); setNetworkErr(null)
    try {
      const r = await authenticatedFetch(`/api/leader-followups/network?from=${netFrom}&to=${netTo}`)
      const d = await r.json()
      if (!d || !d.success) { setNetworkErr(d?.error || 'Erro ao carregar a rede'); setNetwork(null) }
      else setNetwork(d)
    } catch (e: any) {
      setNetworkErr(e?.message || 'Erro de rede'); setNetwork(null)
    } finally { setNetworkLoading(false) }
  }, [authenticatedFetch, netFrom, netTo])

  useEffect(() => { loadNetwork() }, [netFrom, netTo]) // eslint-disable-line react-hooks/exhaustive-deps

  // Opções dos filtros — derivadas dos pares realmente retornados no período.
  const netLeaderOptions = useMemo(() => {
    if (!network) return []
    const byCode = new Map<string, string>()
    for (const p of network.pairs) if (!byCode.has(p.leaderCode)) byCode.set(p.leaderCode, p.leaderName)
    return [...byCode.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [network])
  const netDistritoOptions = useMemo(() => {
    if (!network) return []
    return [...new Set(network.pairs.map(p => p.distrito))].sort()
  }, [network])

  // Pares após filtro de líder/distrito (período já veio filtrado do backend)
  // — ANTES do toggle de categoria da legenda. Serve de base pras contagens
  // da legenda, que continuam mostrando o total mesmo com a categoria oculta.
  const scopedPairs = useMemo(() => {
    if (!network) return []
    return network.pairs.filter(p =>
      (!netLeaderFilter || p.leaderCode === netLeaderFilter) &&
      (!netDistritoFilter || p.distrito === netDistritoFilter)
    )
  }, [network, netLeaderFilter, netDistritoFilter])

  // Depois aplica os toggles de categoria — o que realmente aparece no mapa.
  const filteredPairs = useMemo(() =>
    scopedPairs.filter(p => catVisible[pairCategory(p)]),
  [scopedPairs, catVisible])

  const scopedLeaderCount = useMemo(() => new Set(scopedPairs.map(p => p.leaderCode)).size, [scopedPairs])
  const catCounts = useMemo(() => {
    const c: Record<NetCategory, number> = { new: 0, success: 0, trying: 0, failure: 0, converted: 0 }
    for (const p of scopedPairs) c[pairCategory(p)]++
    return c
  }, [scopedPairs])

  // Uma líder pode ter vários leads no período — um pin só por líder.
  // Some do mapa se o toggle "Líder" da legenda estiver desligado.
  const networkLeaders = useMemo(() => {
    if (!catVisible.leader) return []
    const byCode = new Map<string, NetworkPair>()
    for (const p of filteredPairs) if (!byCode.has(p.leaderCode)) byCode.set(p.leaderCode, p)
    return [...byCode.values()]
  }, [filteredPairs, catVisible.leader])

  // Busca livre (nome do lead/líder, grupo, distrito, telefone) — sobre
  // scopedPairs (já respeita os dropdowns líder/distrito, não os toggles de
  // categoria da legenda: esconder uma categoria não deveria "esconder" ela
  // da busca). 2+ letras ou 4+ dígitos pra evitar matches triviais (DDD sozinho
  // casando com quase todo mundo). null = busca vazia/curta demais; objeto
  // (mesmo com as duas listas vazias) = usuário já digitou o suficiente.
  // Só alimenta a LISTA de resultados — não isola/zoom sozinha (fazia isso a
  // cada tecla e o flyToBounds reiniciava no meio da animação anterior, virava
  // um "loop" de câmera voando sem parar). Zoom só acontece no CLIQUE de um
  // resultado da lista, que reusa o mesmo `detail` do clique num pin.
  //
  // Separado em duas listas (líderes × leads) — um match por nome/telefone de
  // LÍDER dedupe por leaderCode (uma líder pode ter vários leads no período; a
  // lista mostra ela uma vez só). Antes disso era uma lista única de pares, e
  // uma busca por líder aparecia com o nome do LEAD em destaque na linha (a
  // líder só no subtítulo cinza) — parecia que a busca não achava a líder,
  // quando na verdade achava, só não estava visível.
  const searchMatches = useMemo(() => {
    const q = searchTerm.trim()
    if (q.length < 2) return null
    const qNorm = normName(q)
    const qDigits = q.replace(/\D/g, '')
    const leaders = new Map<string, NetworkPair>()
    const leads: NetworkPair[] = []
    for (const p of scopedPairs) {
      const leaderHit = normName(p.leaderName).includes(qNorm) ||
        (qDigits.length >= 4 && !!p.leaderPhone && p.leaderPhone.replace(/\D/g, '').includes(qDigits))
      if (leaderHit && !leaders.has(p.leaderCode)) leaders.set(p.leaderCode, p)

      const leadHit = normName(p.leadName).includes(qNorm) || normName(p.grupo).includes(qNorm) || normName(p.distrito).includes(qNorm) ||
        (qDigits.length >= 4 && !!p.leadPhone && p.leadPhone.replace(/\D/g, '').includes(qDigits))
      if (leadHit) leads.push(p)
    }
    return { leaders: [...leaders.values()], leads }
  }, [searchTerm, scopedPairs])
  const isolating = detail !== null
  const isolatingRef = useRef(isolating)
  useEffect(() => { isolatingRef.current = isolating }, [isolating])

  // Isolamento visual — clicar num pin (ou num resultado da lista de busca)
  // foca ele (+ contraparte) e esmaece o resto. Lead: só ela + a líder dela.
  // Líder: ela + todos os leads dela (respeitando os toggles de categoria já
  // aplicados em filteredPairs).
  const isFocusedPair = (p: NetworkPair): boolean => {
    if (!detail) return true
    if (detail.type === 'lead') return p.assignmentId === detail.pair.assignmentId
    return p.leaderCode === detail.leaderCode
  }
  const isFocusedLeader = (leaderCode: string): boolean => {
    if (!detail) return true
    if (detail.type === 'lead') return leaderCode === detail.pair.leaderCode
    return leaderCode === detail.leaderCode
  }
  const focusPositions = useMemo((): [number, number][] | null => {
    if (!detail) return null
    if (detail.type === 'lead') {
      return [[detail.pair.leaderLat, detail.pair.leaderLng], [detail.pair.leadLat, detail.pair.leadLng]]
    }
    const pairs = filteredPairs.filter(p => p.leaderCode === detail.leaderCode)
    if (pairs.length === 0) return null
    return [[pairs[0].leaderLat, pairs[0].leaderLng], ...pairs.map(p => [p.leadLat, p.leadLng] as [number, number])]
  }, [detail, filteredPairs])
  const allNetworkPositions = useMemo((): [number, number][] => {
    const pts: [number, number][] = networkLeaders.map(l => [l.leaderLat, l.leaderLng])
    filteredPairs.forEach(p => pts.push([p.leadLat, p.leadLng]))
    return pts
  }, [networkLeaders, filteredPairs])

  const filtered = useMemo(() => data?.cidades ?? [], [data])

  // Casa cada cidade filtrada com o contorno do município (nome normalizado).
  // O que não casa (nome fora do padrão, ou município ausente do dataset) vira
  // marcador de ponto — nada some do mapa por falta de contorno.
  const { matchedGeo, unmatched } = useMemo(() => {
    if (!municipios) return { matchedGeo: null as GeoJSON.FeatureCollection | null, unmatched: [] as CoverageCity[] }
    const byName = new Map(municipios.map(f => [normName(f.properties.name), f]))
    const features: MunicipioFeature[] = []
    const unmatched: CoverageCity[] = []
    for (const c of filtered) {
      if (c.lat == null || c.lng == null) continue // sem coordenada nenhuma — nem ponto dá pra plotar
      const feat = byName.get(normName(c.cidade))
      if (feat) features.push({ ...feat, properties: { ...feat.properties, coverage: c } })
      else unmatched.push(c)
    }
    return { matchedGeo: { type: 'FeatureCollection', features } as any, unmatched }
  }, [municipios, filtered])

  const semCoordenada = filtered.filter(c => c.lat == null || c.lng == null).length

  // Borda sempre branca (não a cor do preenchimento) — senão municípios vizinhos
  // com o mesmo status (ex.: dois "sem líder" lado a lado) somem um no outro e
  // viram um blob só, sem separação visual entre os contornos.
  // Maior share de receita entre os municípios plotados — normaliza a escala
  // de cor da camada Receita × Leads (o topo da escala é o município líder).
  const maxReceitaShare = useMemo(
    () => Math.max(...filtered.map(c => c.receitaShare || 0), 0.1),
    [filtered]
  )
  // "Sem líder"/"sem receita" é AUSÊNCIA. Sobre o tile claro o cinza-400 lia
  // como área apagada; sobre o Dark Matter ele vira a coisa mais clara da tela
  // e a ausência passa a gritar mais que a cobertura — a ênfase do mapa
  // inverte. No escuro desce pro cinza-600, neutro sem roubar o olho do verde.
  const cinzaAusencia = isDark ? '#4b5563' : '#9ca3af'
  const styleFeature = (feature?: any) => {
    const c: CoverageCity | undefined = feature?.properties?.coverage
    // Esmaece o contorno de cobertura quando o isolamento líder+lead está
    // ativo — mantém o foco visual nos dois pins. GeoJSON.setStyle propaga
    // opacidade normalmente (só className é que não propaga pós-criação).
    const dimFactor = isolating ? 0.3 : 1
    if (receitaLayer) {
      // Escala de verde pela fatia de receita; âmbar = oportunidade (receita
      // no corte de Pareto e ≤1 lead no ano); cinza claro = sem receita registrada.
      if (c?.oportunidade) {
        return { color: '#fff', weight: 1, opacity: 0.7 * dimFactor, fillColor: '#f59e0b', fillOpacity: 0.65 * dimFactor }
      }
      const share = c?.receitaShare || 0
      const intensity = share > 0 ? 0.18 + 0.55 * Math.sqrt(share / maxReceitaShare) : 0
      return {
        color: '#fff', weight: 1, opacity: 0.7 * dimFactor,
        fillColor: share > 0 ? '#059669' : cinzaAusencia,
        fillOpacity: (share > 0 ? intensity : 0.15) * dimFactor,
      }
    }
    return {
      color: '#fff', weight: 1, opacity: 0.7 * dimFactor,
      fillColor: c?.gap ? cinzaAusencia : '#22c55e',
      fillOpacity: (c?.gap ? 0.5 : 0.35) * dimFactor,
    }
  }
  // Sem rótulo flutuante — a cor já diz tudo (legenda: vermelho=sem líder,
  // verde=com líder). Detalhe por cidade fica no popup ao clicar — EXCETO
  // durante o isolamento líder+lead: polígonos de município cobrem quase o
  // mapa inteiro, então "clicar fora pra sair do zoom" quase sempre acerta
  // uma cidade por baixo. Path.bubblingMouseEvents é true por padrão (o
  // clique já sobe pro mapa e fecha o isolamento certinho); o único problema
  // era o bindPopup automático abrindo JUNTO — aqui a gente assume o clique
  // manualmente e só abre o popup quando NÃO tem isolamento ativo.
  const onEachFeature = (feature: any, layer: Layer) => {
    const c: CoverageCity = feature.properties.coverage
    layer.on('click', (e: any) => {
      if (isolatingRef.current) return
      layer.bindPopup(CoveragePopupHtml(c)).openPopup(e.latlng)
    })
  }

  // KPIs do painel — sobre a REDE (leads x consultoras no período), não sobre
  // cobertura estrutural de município (isso já fica no legend do polígono).
  const conversionRate = scopedPairs.length ? (catCounts.converted / scopedPairs.length) * 100 : null
  const kpiCards = network ? [
    { label: 'Leads no período', value: scopedPairs.length, cls: 'text-foreground' },
    { label: 'Líderes ativas', value: scopedLeaderCount, cls: 'text-purple-500' },
    { label: 'Virou consultora', value: catCounts.converted, cls: 'text-blue-500' },
    { label: 'Taxa de conversão', value: conversionRate != null ? `${conversionRate.toFixed(1)}%` : '—', cls: 'text-emerald-500' },
  ] : []

  const tileUrl = basemapUrl(isDark)

  return (
    <div className="h-full flex flex-col p-4 gap-4 neu-forms">
      {loading && !data && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
          <FiLoader className="w-6 h-6 animate-spin text-violet-500" />
          <span className="text-sm">Carregando cobertura...</span>
        </div>
      )}

      {err && (
        <div className="shrink-0 bg-red-500/5 border border-red-500/30 rounded-xl p-5 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">Erro ao carregar a cobertura</h3>
            <p className="text-xs text-muted-foreground font-mono break-all">{err}</p>
          </div>
        </div>
      )}

      {/* O MAPA FICA SEMPRE MONTADO. Antes este bloco era `data && !loading`,
          e o Leaflet inteiro era destruído e recriado a cada carga — o mesmo
          antipadrão que o Mapa de Ativas já documenta ter corrigido
          (MapaDistribuicao.tsx:2455, bug do "tremendo em loop", 18/08). Estado
          de carga e vazio agora são overlay POR CIMA, não troca de árvore. */}
      {(
        <div className="flex-1 min-h-0 rounded-xl border border-border overflow-hidden relative bg-muted">
          <MapContainer center={CAMPO_GRANDE} zoom={7} zoomControl={false} className="w-full h-full">
            <ZoomControl position="bottomright" />
            {/* CAMPO_GRANDE acima é só o valor de partida enquanto a config não
                chega; quem manda de verdade é este efeito. */}
            <PracaViewEffect center={mapCfg?.center ?? null} />
            {/* `key={tileUrl}`: sem ele a troca de tema não remonta o layer.
                maxZoom subiu de 18 p/ 20, em paridade com o Mapa de Ativas. */}
            <TileLayer key={tileUrl} attribution={BASEMAP_ATTRIB} url={tileUrl} maxZoom={20} />

            {matchedGeo && matchedGeo.features.length > 0 && (
              // key inclui a camada ativa: GeoJSON só relê style na CRIAÇÃO
              // (mesma quirk do Polyline — ver comentário abaixo), então trocar
              // cobertura↔receita precisa forçar remount.
              <GeoJSON key={`${receitaLayer ? 'rx' : 'cov'}|${matchedGeo.features.map((f: any) => f.properties.coverage.cidade).join('|')}`}
                data={matchedGeo} style={styleFeature} onEachFeature={onEachFeature} />
            )}

            {unmatched.map(c => (
              <Marker key={c.cidade} position={[c.lat!, c.lng!]} icon={cityDotIcon(c.gap ? '#9ca3af' : '#22c55e', isolating)}>
                <Popup><div dangerouslySetInnerHTML={{ __html: CoveragePopupHtml(c) }} /></Popup>
              </Marker>
            ))}

            {/* Rede líder↔lead: mesma camada, sobre o mesmo mapa de cobertura.
                className vai como prop DIRETA (não dentro de pathOptions) — o
                react-leaflet só aplica pathOptions via setStyle() depois da
                criação do path, e o Leaflet só adiciona a className no
                _initPath (na criação); setStyle nunca mexe na classList, então
                pathOptions.className nunca chegava a aplicar (bug real: linha
                nunca animava). className direto entra nas options do
                construtor e funciona. MESMO MOTIVO pelo qual o key inclui o
                estado "dim" abaixo: color/opacity/className só são lidos na
                CRIAÇÃO do Polyline (updatePolyline do react-leaflet só reage a
                mudança de `positions`) — sem isso, o esmaecimento no clique
                nunca apareceria, teria que dar refresh na página. Esmaecimento
                é só via `opacity` (sem blur — em ~500 pins/linhas simultâneas
                o blur disparava a CPU e travava o zoom). */}
            {filteredPairs.map(p => {
              const dim = isolating && !isFocusedPair(p)
              return (
                <Polyline key={`${p.assignmentId}-${dim ? 'dim' : 'on'}`}
                  positions={[[p.leaderLat, p.leaderLng], [p.leadLat, p.leadLng]]}
                  color={leadColor(p)} weight={2} opacity={dim ? 0.12 : 0.85}
                  className="network-line-flow" />
              )
            })}
            {/* Clique no pin abre o painel flutuante de detalhes (substitui o
                Popup raso do Leaflet) e isola líder+lead(s) com zoom — ver
                painel e NetworkFocusEffect logo após o MapContainer. Ícone
                (não Polyline) reage certo a mudança de prop via setIcon do
                react-leaflet — não precisa do truque de key. */}
            {networkLeaders.map(l => (
              <Marker key={l.leaderCode} position={[l.leaderLat, l.leaderLng]} icon={dotIcon(LEADER_COLOR, isolating && !isFocusedLeader(l.leaderCode))}
                zIndexOffset={isFocusedLeader(l.leaderCode) ? 1000 : 0}
                eventHandlers={{ click: () => { setSearchTerm(''); setDetail({ type: 'leader', leaderCode: l.leaderCode }) } }} />
            ))}
            {filteredPairs.map(p => (
              <Marker key={`lead-${p.assignmentId}`} position={[p.leadLat, p.leadLng]} icon={leadIcon(leadColor(p), isolating && !isFocusedPair(p))}
                zIndexOffset={isFocusedPair(p) ? 1000 : 0}
                eventHandlers={{ click: () => { setSearchTerm(''); setDetail({ type: 'lead', pair: p }) } }} />
            ))}
            <NetworkFocusEffect focusPositions={focusPositions} allPositions={allNetworkPositions} onBackgroundClick={() => { setDetail(null); setSearchTerm('') }} />
          </MapContainer>

          {/* Painel único flutuante — KPIs, legenda de cobertura por município,
              filtros e legenda-toggle da rede líder/lead. Clicar numa linha da
              legenda da rede liga/desliga aquela categoria no mapa (pin + laço).
              Sem header/toolbar acima do mapa — tudo mora aqui dentro. */}
          <div className="absolute top-3 left-3 z-[1000] flex items-start gap-2">
          <div className="neu-float rounded-lg px-3 py-2 text-[11px] space-y-1.5 shadow-lg w-[min(15rem,calc(100vw-1.5rem))] max-h-[calc(100vh-8rem)] overflow-y-auto">
            {kpiCards.length > 0 && (
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1.5 border-b border-border">
                {kpiCards.map(k => (
                  <div key={k.label}>
                    <div className="text-muted-foreground leading-tight">{k.label}</div>
                    <div className={`font-bold tabular-nums ${k.cls}`}>{k.value}</div>
                  </div>
                ))}
              </div>
            )}
            {!receitaLayer ? (
              <>
                <div className="flex items-center gap-2">
                  {/* Acompanha o cinzaAusencia do polígono — legenda que não bate
                      com o mapa é pior que legenda nenhuma. */}
                  <span className="w-3 h-3 rounded-sm bg-gray-400 dark:bg-gray-600 shrink-0" />
                  <span className="text-foreground">Área sem líder ({filtered.filter(c => c.gap).length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-green-500/40 border border-green-500 shrink-0" />
                  <span className="text-foreground">Área com líder ({filtered.filter(c => !c.gap).length})</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: 'linear-gradient(90deg, rgba(5,150,105,0.2), rgba(5,150,105,0.75))' }} />
                  <span className="text-foreground">Receita {new Date().getFullYear()} (mais escuro = maior)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-amber-500 shrink-0" />
                  <span className="text-foreground" title="Município no corte de Pareto da receita com ≤1 lead no ano — receita alta, captação quase nula">
                    Oportunidade ({filtered.filter(c => c.oportunidade).length})
                  </span>
                </div>
              </>
            )}
            <button onClick={() => setReceitaLayer(v => !v)}
              title="Alternar entre cobertura de liderança e receita × leads por município"
              className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
              <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-emerald-600" />
              <span className="text-foreground flex-1">Receita × Leads</span>
              <span className="relative inline-flex h-4 w-7 shrink-0 items-center rounded-full neu-switch transition-colors"
                style={{ backgroundColor: receitaLayer ? '#059669' : 'rgba(148,163,184,0.35)' }}>
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white neu-knob transition-transform ${receitaLayer ? 'translate-x-[14px]' : 'translate-x-0.5'}`} />
              </span>
            </button>

            <div className="border-t border-border pt-1.5 space-y-1.5">
              <div className="flex items-center gap-1.5 font-medium text-muted-foreground">
                <FiShare2 className="w-3.5 h-3.5 text-violet-500 shrink-0" /> Filtrar rede
              </div>
                <div className="relative">
                  <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
                  <input
                    type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar lead, líder, grupo, distrito, telefone…"
                    className="w-full pl-6 pr-6 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} title="Limpar busca"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <FiX className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <select value={netLeaderFilter} onChange={e => setNetLeaderFilter(e.target.value)}
                  className="w-full px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500">
                  <option value="">Todas as líderes</option>
                  {netLeaderOptions.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
                <select value={netDistritoFilter} onChange={e => setNetDistritoFilter(e.target.value)}
                  className="w-full px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500">
                  <option value="">Todos os distritos</option>
                  {netDistritoOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="shrink-0">De</span>
                  <input type="date" value={netFrom} max={netTo} onChange={e => setNetFrom(e.target.value)}
                    className="flex-1 min-w-0 px-1.5 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="shrink-0">até</span>
                  <input type="date" value={netTo} min={netFrom} onChange={e => setNetTo(e.target.value)}
                    className="flex-1 min-w-0 px-1.5 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div className="flex items-center gap-1">
                  {[30, 60, 90, 120].map(days => {
                    const active = netTo === isoDate(new Date()) && netFrom === isoDate(new Date(Date.now() - days * 86400000))
                    return (
                      <button key={days} onClick={() => { setNetTo(isoDate(new Date())); setNetFrom(isoDate(new Date(Date.now() - days * 86400000))) }}
                        className={`flex-1 px-1 py-1 rounded border transition-colors ${
                          active ? 'bg-violet-500/15 border-violet-500/40 text-violet-600 dark:text-violet-400' : 'border-border text-muted-foreground hover:bg-muted/50'
                        }`}>
                        {days}d
                      </button>
                    )
                  })}
                </div>
                {(netLeaderFilter || netDistritoFilter) && (
                  <button onClick={() => { setNetLeaderFilter(''); setNetDistritoFilter('') }}
                    className="text-violet-600 dark:text-violet-400 hover:underline">
                    Limpar filtros
                  </button>
                )}
              </div>

            {network && (
              <div className="border-t border-border pt-1.5 space-y-0.5">
                {([
                  { key: 'leader' as const, color: LEADER_COLOR, label: 'Líder', count: scopedLeaderCount },
                  { key: 'new' as const, color: NEW_COLOR, label: 'Lead novo / sem resposta', count: catCounts.new },
                  { key: 'success' as const, color: SUCCESS_COLOR, label: 'Lead deu certo', count: catCounts.success },
                  { key: 'trying' as const, color: TRYING_COLOR, label: 'Lead ainda tentando', count: catCounts.trying },
                  { key: 'failure' as const, color: FAILURE_COLOR, label: 'Lead não deu certo', count: catCounts.failure },
                  { key: 'converted' as const, color: CONVERTED_COLOR, label: 'Virou consultora', count: catCounts.converted },
                ]).map(row => {
                  const on = catVisible[row.key]
                  return (
                    <button key={row.key} onClick={() => toggleCat(row.key)} title="Mostrar/esconder no mapa"
                      className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                      <span className={`text-foreground flex-1 truncate ${on ? '' : 'opacity-45'}`}>{row.label} ({row.count})</span>
                      <span className="relative inline-flex h-4 w-7 shrink-0 items-center rounded-full neu-switch transition-colors"
                        style={{ backgroundColor: on ? row.color : 'rgba(148,163,184,0.35)' }}>
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white neu-knob transition-transform ${on ? 'translate-x-[14px]' : 'translate-x-0.5'}`} />
                      </span>
                    </button>
                  )
                })}
                <div className="text-muted-foreground pt-1">
                  Linha animada = laço líder↔lead · {filteredPairs.length} lead{filteredPairs.length === 1 ? '' : 's'} exibido{filteredPairs.length === 1 ? '' : 's'}
                </div>
              </div>
            )}
          </div>

          {/* Lista de resultados da busca — caixa irmã da de filtros, mesmo
              estilo. Só lista; o zoom/isolamento no mapa só acontece quando o
              usuário CLICA um resultado (reusa o `detail` do clique num pin —
              nada de zoom automático a cada tecla, era isso que causava o
              mapa "voar" sem parar enquanto digitava). */}
          {searchMatches !== null && (() => {
            const total = searchMatches.leaders.length + searchMatches.leads.length
            return (
            <div className="neu-float rounded-lg shadow-lg w-[min(16rem,calc(100vw-1.5rem))] max-h-[70vh] overflow-y-auto text-[11px]">
              <div className="sticky top-0 neu-bar-round px-3 py-2 border-b border-border font-medium text-muted-foreground">
                {total === 0 ? 'Nenhum resultado' : `${total} resultado${total === 1 ? '' : 's'}`}
              </div>
              {total > 0 && (
                <div className="p-1">
                  {searchMatches.leaders.length > 0 && (
                    <>
                      <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Líderes</div>
                      {searchMatches.leaders.map(p => (
                        <button key={p.leaderCode} onClick={() => { setDetail({ type: 'leader', leaderCode: p.leaderCode }); setSearchTerm('') }}
                          className="w-full flex items-start gap-2 text-left rounded px-2 py-1.5 hover:bg-muted/50">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style={{ backgroundColor: LEADER_COLOR }} />
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-foreground truncate">{p.leaderName}</span>
                            <span className="block text-muted-foreground truncate">{p.distrito} · {p.grupo}</span>
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  {searchMatches.leads.length > 0 && (
                    <>
                      <div className="px-2 pt-1.5 pb-0.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Leads</div>
                      {searchMatches.leads.slice(0, 30).map(p => (
                        <button key={p.assignmentId} onClick={() => { setDetail({ type: 'lead', pair: p }); setSearchTerm('') }}
                          className="w-full flex items-start gap-2 text-left rounded px-2 py-1.5 hover:bg-muted/50">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style={{ backgroundColor: leadColor(p) }} />
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-foreground truncate">{p.leadName}</span>
                            <span className="block text-muted-foreground truncate">líder {p.leaderName} · {p.distrito}</span>
                          </span>
                        </button>
                      ))}
                      {searchMatches.leads.length > 30 && (
                        <div className="px-2 py-1.5 text-muted-foreground">+{searchMatches.leads.length - 30} — refine a busca</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            )
          })()}
          </div>

          {/* Painel de detalhes — abre ao clicar num pin de líder ou de lead. */}
          {detail && (
            <div className="absolute top-3 right-3 z-[1100] neu-float rounded-lg w-[min(18rem,calc(100vw-1.5rem))] max-h-[calc(100%-24px)] overflow-y-auto text-[11px]">
              <div className="sticky top-0 neu-bar-round flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="font-semibold text-foreground">{detail.type === 'lead' ? 'Detalhes do lead' : 'Detalhes da líder'}</span>
                <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-foreground">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                {detail.type === 'lead' ? (() => {
                  const p = detail.pair
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: leadColor(p) }} />
                        <span className="font-semibold text-foreground">{p.leadName}</span>
                      </div>
                      <div className="font-medium" style={{ color: leadColor(p) }}>{outcomeLabel(p)}</div>
                      {p.convertedOracle && p.convertedAt && (
                        <div className="text-muted-foreground">Virou consultora em {fmtDate(p.convertedAt)}</div>
                      )}
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div><span className="text-muted-foreground">Telefone: </span>{p.leadPhone ? formatPhone(p.leadPhone) : '—'}</div>
                        {p.leadCpf && <div><span className="text-muted-foreground">CPF: </span>{p.leadCpf}</div>}
                        <div><span className="text-muted-foreground">Endereço: </span>{p.leadAddress || (p.leadCep ? `CEP ${p.leadCep}` : '—')}</div>
                        {p.leadCep && p.leadAddress && <div><span className="text-muted-foreground">CEP: </span>{p.leadCep}</div>}
                        {p.leadCity && <div><span className="text-muted-foreground">Cidade: </span>{p.leadCity}</div>}
                      </div>
                      {(p.leadMotivation || (p.leadInterests && p.leadInterests.length > 0)) && (
                        <div className="border-t border-border pt-1.5 space-y-1">
                          {p.leadMotivation && <div><span className="text-muted-foreground">Motivação: </span>{p.leadMotivation}</div>}
                          {p.leadInterests && p.leadInterests.length > 0 && (
                            <div><span className="text-muted-foreground">Interesses: </span>{p.leadInterests.join(', ')}</div>
                          )}
                        </div>
                      )}
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div>
                          <span className="text-muted-foreground">Líder atribuída: </span>
                          <button onClick={() => setDetail({ type: 'leader', leaderCode: p.leaderCode })} className="text-violet-600 dark:text-violet-400 hover:underline">
                            {p.leaderName}
                          </button>
                        </div>
                        <div><span className="text-muted-foreground">Distrito/Grupo: </span>{p.distrito} · {p.grupo}</div>
                        <div><span className="text-muted-foreground">Encaminhado em: </span>{fmtDateTime(p.assignedAt)}</div>
                        <div><span className="text-muted-foreground">Primeira mensagem: </span>{fmtDateTime(p.firstMessageAt)}</div>
                        {p.cadastroCompletoAt && <div><span className="text-muted-foreground">Cadastro completo em: </span>{fmtDate(p.cadastroCompletoAt)}</div>}
                      </div>
                    </>
                  )
                })() : (() => {
                  const pairs = scopedPairs.filter(p => p.leaderCode === detail.leaderCode)
                    .slice().sort((a, b) => (b.assignedAt || '').localeCompare(a.assignedAt || ''))
                  const first = pairs[0]
                  if (!first) return <div className="text-muted-foreground">Sem leads no período selecionado.</div>
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: LEADER_COLOR }} />
                        <span className="font-semibold text-foreground">{first.leaderName}</span>
                      </div>
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div><span className="text-muted-foreground">Código: </span>{first.leaderCode}</div>
                        <div><span className="text-muted-foreground">Telefone: </span>{first.leaderPhone ? formatPhone(first.leaderPhone) : '—'}</div>
                        <div><span className="text-muted-foreground">Distrito/Grupo: </span>{first.distrito} · {first.grupo}</div>
                      </div>
                      <div className="border-t border-border pt-1.5">
                        <div className="text-muted-foreground mb-1">Leads recebidos ({pairs.length})</div>
                        <div className="space-y-0.5">
                          {pairs.map(p => {
                            const isOpen = expandedLead === p.assignmentId
                            const timeline = leadTimeline(p)
                            return (
                              <div key={p.assignmentId}>
                                <button onClick={() => setExpandedLead(isOpen ? null : p.assignmentId)}
                                  className="w-full flex items-center gap-1.5 text-left rounded px-1 py-1 hover:bg-muted/50">
                                  {isOpen ? <FiChevronDown className="w-3 h-3 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: leadColor(p) }} />
                                  <span className="flex-1 truncate text-foreground">{p.leadName}</span>
                                  <span className="text-muted-foreground shrink-0">{fmtDate(p.assignedAt)}</span>
                                </button>
                                {isOpen && (
                                  <div className="ml-[18px] mb-1 pl-2 border-l border-border space-y-1">
                                    {timeline.length === 0 && <div className="text-muted-foreground">Sem datas registradas.</div>}
                                    {timeline.map((t, i) => (
                                      <div key={i}><span className="text-muted-foreground">{t.label}: </span>{fmtDateTime(t.date)}</div>
                                    ))}
                                    <button onClick={() => setDetail({ type: 'lead', pair: p })}
                                      className="text-violet-600 dark:text-violet-400 hover:underline">
                                      Ver detalhes completos
                                    </button>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>
          )}

          {/* ESTADOS VAZIO E DE CARGA — como OVERLAY, nunca trocando a árvore.
              Antes, um período sem pares mostrava tiles em branco sobre o Mato
              Grosso do Sul e mais nada: nenhuma explicação de por quê. O Mapa
              de Ativas já distingue três casos (MapaDistribuicao.tsx:2650) e
              este painel tinha ficado para trás. Overlay porque desmontar o
              mapa é o bug que acabamos de corrigir logo acima. */}
          {loading && (
            <div className="absolute inset-0 z-[900] flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <FiLoader className="w-4 h-4 animate-spin" /> Carregando cobertura…
              </div>
            </div>
          )}
          {!loading && data && municipios && (matchedGeo?.features.length ?? 0) === 0 && unmatched.length === 0 && (
            <div className="absolute inset-0 z-[900] flex items-center justify-center bg-background/70">
              <div className="max-w-xs px-4 text-center text-[12px] text-muted-foreground">
                <div className="mb-1 font-medium text-foreground">Nenhum município para mostrar</div>
                <div>Não há consultora nem líder com cidade preenchida no cadastro, ou o filtro atual não deixou nada. Confira o sincronismo com o Oracle.</div>
              </div>
            </div>
          )}
          {!loading && network && !networkLoading && filteredPairs.length === 0 && (
            <div className="absolute bottom-12 left-1/2 z-[900] -translate-x-1/2 neu-float-2 backdrop-blur border border-border rounded-lg px-3 py-2 text-center text-[11px] text-muted-foreground shadow-lg">
              Nenhum encaminhamento líder↔lead no período — ou todas as categorias
              estão desligadas na legenda.
            </div>
          )}

          <div className="absolute bottom-3 left-3 z-[1000] neu-float-2 backdrop-blur border border-border rounded-lg px-3 py-1.5 text-[11px] text-muted-foreground shadow-lg space-y-0.5">
            {!municipios && <div>Carregando contornos…</div>}
            {semCoordenada > 0 && <div>{semCoordenada} cidade{semCoordenada === 1 ? '' : 's'} sem coordenada</div>}
            {networkLoading && <div className="flex items-center gap-1.5"><FiLoader className="w-3 h-3 animate-spin" /> Geocodificando leads da rede...</div>}
            {network && !networkLoading && (
              <div>
                {filteredPairs.length} de {network.resolved} leads da rede exibidos ({network.resolved} de {network.total} geolocalizados) · {new Date(netFrom + 'T12:00:00').toLocaleDateString('pt-BR')}–{new Date(netTo + 'T12:00:00').toLocaleDateString('pt-BR')}
              </div>
            )}
            {/* O backend corta em 1500 encaminhamentos. Antes o `total` já vinha
                pós-corte, então a tela dizia "1500 de 1500" e os que sobravam
                sumiam em silêncio. Agora o corte é dito em voz alta. */}
            {network?.truncated && !networkLoading && (
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <FiAlertCircle className="w-3 h-3 shrink-0" />
                Período excede o teto de {network.limit ?? 1500}: mostrando os mais
                recentes. Estreite as datas para ver o resto.
              </div>
            )}
          </div>
          {networkErr && (
            <div className="absolute bottom-3 right-3 z-[1000] bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-1.5 text-[11px] text-red-600 dark:text-red-400 shadow-lg max-w-xs">
              Rede líder↔lead: {networkErr}
            </div>
          )}
        </div>
      )}

    </div>
  )
}
